import { useCallback, useEffect, useMemo, useState } from 'react';
import { Form, Select } from 'antd';

import { errorMsg } from '../constants/messages';
import { InputInterface } from '../utils/forms/FormInterface';

const { Option } = Select;

import './style.scss';
import debounceFunction from '../utils/debounce/Debounce';

interface SelectInputProps {
  formInput: InputInterface;
  setFormData?: any;
  form: any;
  index: number;
  isCreateMode?: boolean;
  setHide?: any;
  // eslint-disable-next-line no-unused-vars
  setDynamicValidation: (value: any) => void;
  setHideCron?: any;
  setHideMaxAMount?: any;
}

const getOptionLabel = (option: any) =>
  String(option?.name ?? option?.label ?? option?.value ?? '');

const SelectInput = (props: SelectInputProps) => {
  const { formInput, setFormData, form, index, isCreateMode, setDynamicValidation } = props;
  const {
    fieldType: { options = [], allowCustom },
  } = formInput as any;

  const currentItemId = form.getFieldValue(formInput.name);
  const [searchValue, setSearchValue] = useState('');
  const [customOptions, setCustomOptions] = useState<any[]>([]);

  /**
   * this function updates the list of form fields to be shown based on the item that is selected from the dropdown menu of items
   */
  const updateOptions = useCallback((optionTypeArray?: any, setFormData?: any) => {
    optionTypeArray &&
      setFormData?.((formData: any) => [
        ...formData.filter((formDatum: any) => !formDatum.addedDynamically),
        ...optionTypeArray.map(
          (optionType: {
            displayName: string;
            options: { id: string; displayName: string }[];
          }) => ({
            addedDynamically: true,
            name: `optionType-${optionType.displayName}`,
            label: optionType.displayName,
            fieldType: {
              name: 'select',
              options: optionType.options.map(option => ({
                value: option.id,
                name: option.displayName,
              })),
            },
            readonly: !isCreateMode,
            required: true,
            errorMsg: errorMsg,
            placeholder: `Click to select a ${optionType.displayName.toLowerCase()}`,
            tableMeta: { title: 'test', enableSort: false, allowAddRecordClick: false },
            isTableHeader: false,
            isAccordian: true,
            default: '',
          })
        ),
      ]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * From this useEffect the above defined function to update the input form fields is called on the initial render
   */
  useEffect(() => {
    const item = options.find((option: { value: number }) => option.value === currentItemId);
    const optionTypeArray = item?.additionalData;

    updateOptions(optionTypeArray, setFormData);
  }, [currentItemId, options, setFormData, updateOptions]);

  // Keep an already-saved custom value visible in the dropdown
  useEffect(() => {
    if (!allowCustom || currentItemId == null || currentItemId === '') return;
    const existsInPreset = options.some(
      (option: any) => String(option.value) === String(currentItemId)
    );
    if (existsInPreset) return;
    setCustomOptions(prev => {
      const existsInCustom = prev.some(
        (option: any) => String(option.value) === String(currentItemId)
      );
      if (existsInCustom) return prev;
      return [...prev, { value: currentItemId, name: String(currentItemId) }];
    });
  }, [allowCustom, currentItemId, options]);

  const mergedOptions = useMemo(() => {
    const seen = new Set<string>();
    const result: any[] = [];
    [...options, ...customOptions].forEach((option: any) => {
      const key = String(option?.value ?? '');
      if (!key || seen.has(key.toLowerCase())) return;
      seen.add(key.toLowerCase());
      result.push({
        ...option,
        name: getOptionLabel(option),
        value: option.value,
      });
    });
    return result;
  }, [options, customOptions]);

  const trimmedSearch = searchValue.trim();
  const canAddCustom =
    Boolean(allowCustom) &&
    Boolean(trimmedSearch) &&
    !mergedOptions.some(
      (option: any) => getOptionLabel(option).toLowerCase() === trimmedSearch.toLowerCase()
    );

  /**
   * This function updates the form fields when the item is changed in the dropdown
   */
  const changeHandler = (value: any, record: any) => {
    setDynamicValidation(value);
    setSearchValue('');

    if (allowCustom && value != null && value !== '') {
      const exists = mergedOptions.some(
        (option: any) => String(option.value).toLowerCase() === String(value).toLowerCase()
      );
      if (!exists) {
        setCustomOptions(prev => [...prev, { value, name: String(value) }]);
      }
    }

    const { additionaldata: optionTypeArray } = record || {};

    updateOptions(optionTypeArray, setFormData);
  };

  /**
   * This function applies a debounce to the onChange handler of the select component in order to improve search performance
   */
  // Define the debounced search function separately
  const debouncedSearch = (value: string, options: any[], formInput: any, form: any) => {
    const filteredOptions = options.filter(
      (option: any) =>
        getOptionLabel(option).toLowerCase().indexOf(value.toLowerCase()) !== -1
    );
    formInput.fieldType.options = filteredOptions;

    // Trigger a re-render to update the select options
    form.setFieldsValue({
      [formInput.name]: undefined,
    });
  };

  // Create a memoized function that wraps the debounced function using useCallback
  // eslint-disable-next-line react-hooks/exhaustive-deps, no-unused-vars
  const handleSearch = useCallback(debounceFunction(debouncedSearch, 500), []);

  return (
    <Form.Item
      key={index}
      label={formInput.label}
      name={formInput.name}
      className="text"
      wrapperCol={{ span: 8, offset: 4 }}
      labelCol={{ span: 4 }}
      labelAlign="left"
      rules={[
        {
          required: formInput.required,
          message: formInput.errorMsg,
        },
      ]}
    >
      <Select
        showSearch
        allowClear
        optionFilterProp="children"
        onChange={changeHandler}
        onSearch={value => {
          setSearchValue(value);
          if (!allowCustom) {
            handleSearch(value, options, formInput, form);
          }
        }}
        filterOption={(input, option) =>
          String(option?.children ?? '')
            .toLowerCase()
            .includes(input.toLowerCase())
        }
        placeholder={formInput.placeholder}
        disabled={formInput.readonly}
        style={{ width: '100%' }}
        dropdownClassName="select-checkbox-dropdown"
        notFoundContent={
          allowCustom && trimmedSearch
            ? `Press enter or select "Add ${trimmedSearch}"`
            : undefined
        }
      >
        {canAddCustom && (
          <Option key={`__custom__${trimmedSearch}`} value={trimmedSearch}>
            {`Add "${trimmedSearch}"`}
          </Option>
        )}
        {mergedOptions.map((obj: any, index: number) => (
          <Option key={`${obj.value}-${index}`} value={obj.value} additionaldata={obj.additionalData}>
            {obj.name}
          </Option>
        ))}
      </Select>
    </Form.Item>
  );
};

export default SelectInput;
